<script setup lang="ts">
import {computed} from "vue"
import GradeLevelTagLabel from "~components/GradeLevelTagLabel.vue"
import {useGetJson} from "@/common/utils/fetch"

const props = defineProps<{lessonId: string | number}>()
const emit = defineEmits(["selected"])

// named so it won't be confused with any official Lesson type which is what we'll use later
type LocalLesson = {
  lessonid: number
  gradeLabel: string | null
  storygenre: string | null
  subject: string | null
  musicgenre: string | null
  ltitle: string | null
  logo_url: string | null
  wordspermin: string | null
  lexile_measure: string | null
}

type LocalLessonResponse = {
  success: boolean
  data?: LocalLesson
}

const response = useGetJson<LocalLessonResponse>(computed(() => `/service/objects/lesson/${props.lessonId}`))
const lesson = computed(() => response.value?.data)

const genreLine = computed(() => {
  if (!lesson.value) return ""
  const {storygenre, subject, musicgenre} = lesson.value
  return [storygenre, subject, musicgenre].filter(x => x).join(" / ")
})

function clickLesson() {
  if (!lesson.value) return
  emit("selected", lesson.value)
}
</script>

<template>
  <div>
    <div v-if="lesson" class="lesson-tile" @click="clickLesson()">
      <slot name="logo" v-bind="{lesson}">
        <div class="img-container">
          <img class="img-responsive" :src="lesson.logo_url ?? ''" :alt="lesson.ltitle ?? ''">
        </div>
      </slot>
      <slot name="details" v-bind="{lesson}">
        <div class="lesson-details">
          <div class="title-row">
            <span class="lesson-title">{{lesson.ltitle}}</span>
          </div>
          <div class="gradelevel">
            <GradeLevelTagLabel v-if="lesson.gradeLabel" :grade="lesson.gradeLabel" />
          </div>
          <span class="genre">{{ genreLine }}</span>
          <span class="lexile-wpm"><b>Lexile:</b> {{ lesson.lexile_measure }}  <b>WPM:</b> {{ lesson.wordspermin }}</span>
        </div>
      </slot>
    </div>
    <div v-else>
      <slot name="loading" v-bind="{lessonId}">
        <div class="loading">
          <h3>Loading...</h3>
        </div>
      </slot>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.lesson-tile {
  display: flex;
  cursor: pointer;

  .img-container {
    width: 80px;
  }

  .lesson-details {
    display: flex;
    flex-direction: column;
    margin-left: 10px;

    .lesson-title {
      font-weight: bold;
      font-size: 2rem;
    }
  }

  &:hover {
    border-radius: 4px;
    background-color: #dcdcdc;
  }
}

.loading {
  min-height: 80px;
  border: 1px solid #333333;
  display: flex;
  margin: 2px;
  border-radius: 4px;

  h3 {
    margin: auto;
  }
}
</style>
